import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronDown, ChevronRight, Plus } from 'react-feather';
import { useAppDispatch, useAppSelector } from '../../../../../ReaduxToolkit/Hooks';
import { updateElements } from '../store/formDocumentSlice';
import { setSelection } from '../store/formDesignerUiSlice';
import { VARIABLE_DRAG_TYPE, dragState } from '../Canvas/PageCanvas';
import { bindingsWith, boundFieldsOf, collectTemplateFields, collectVariableFieldKeys } from '../fieldUsage';
import { SectionFields } from '../masterFields';
import { FormElement } from '../store/types';

interface Props {
  masterFields: SectionFields;
}

// Lists every fillable spot found on the template, in reading order. Each row is a
// drop zone and also expands (toggle) to show all master fields, in order, to pick from.
export default function TemplateFieldsPanel({ masterFields }: Props) {
  const dispatch = useAppDispatch();
  const doc = useAppSelector((s) => s.FormDocument.present);
  const selectedIds = useAppSelector((s) => s.FormDesignerUi.selectedIds);
  const [overId, setOverId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [rowQuery, setRowQuery] = useState('');
  const fields = useMemo(() => collectTemplateFields(doc), [doc]);
  const used = useMemo(() => new Set(collectVariableFieldKeys(doc)), [doc]);
  const panelRef = useRef<HTMLDivElement>(null);

  // Selecting a field on the template scrolls this list to its row and keeps it there
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || selectedIds.length !== 1) return;
    const row = panel.querySelector<HTMLElement>(`[data-row-id="${selectedIds[0]}"]`);
    if (!row) return;
    panel.scrollTo({ top: row.offsetTop - panel.clientHeight / 2 + row.offsetHeight / 2, behavior: 'smooth' });
  }, [selectedIds]);

  if (fields.length === 0) return null;

  const boundCount = fields.filter((f) => f.element.bindings?.dynamicField).length;

  const sections = Object.entries(masterFields)
    .map(([sectionKey, list]) => {
      const names = (Array.isArray(list) ? list : [])
        .map((f) => (typeof f === 'object' ? f.field_name : String(f)))
        .filter((n) => n.toLowerCase().includes(query.toLowerCase()));
      return [sectionKey, names] as const;
    })
    .filter(([, names]) => names.length > 0);

  // A row can map several master fields. A group of options (Yes/No...) is one row: every member gets the same list.
  const applyList = (pageId: string, members: FormElement[], list: { fieldKey: string; sectionKey?: string }[]) => {
    dispatch(updateElements({ pageId, changes: members.map((m) => ({ id: m.id, changes: { bindings: bindingsWith(m, list) } })) }));
    dispatch(setSelection([members[0].id]));
  };

  // Adds the field to the row (does nothing if it is already there)
  const bind = (pageId: string, members: FormElement[], fieldKey: string, sectionKey?: string) => {
    const cur = boundFieldsOf(members[0]);
    if (cur.some((c) => c.fieldKey === fieldKey)) return;
    applyList(pageId, members, [...cur, { fieldKey, sectionKey }]);
  };

  const unbindOne = (pageId: string, members: FormElement[], fieldKey: string) => {
    applyList(pageId, members, boundFieldsOf(members[0]).filter((c) => c.fieldKey !== fieldKey));
  };

  const focusField = (id: string) => {
    dispatch(setSelection([id]));
    document
      .querySelector(`[data-element-id="${id}"]`)
      ?.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
  };

  return (
    <div className="fd-fields-panel" ref={panelRef}>
      <div className="fd-fields-sticky">
        <div className="fd-fields-panel-title">
          Template Fields <span className="fd-fields-count">{boundCount}/{fields.length}</span>
        </div>
        <input
          className="form-control form-control-sm mt-1"
          placeholder="Search template fields..."
          value={rowQuery}
          onChange={(e) => setRowQuery(e.target.value)}
        />
      </div>
      <div className="fd-toolbox-hint">Drop fields on a row, or expand a row and tick several. Use + Add field for more than one.</div>
      <ul className="fd-fields-list">
        {fields.map((f, i) => {
          const rq = rowQuery.trim().toLowerCase();
          if (rq && !`${f.hint} ${boundFieldsOf(f.element).map((b) => b.fieldKey).join(' ')}`.toLowerCase().includes(rq) && openId !== f.element.id) return null;
          const el = f.element;
          const id = el.id;
          const boundList = boundFieldsOf(el);
          const open = openId === id;
          const cls = ['fd-fields-row'];
          if (boundList.length > 0) cls.push('fd-fields-row-bound');
          if (overId === id) cls.push('fd-fields-row-over');
          if (selectedIds.includes(id)) cls.push('fd-fields-row-selected');
          return (
            <li
              key={id}
              data-row-id={id}
              className={cls.join(' ')}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
              }}
              onDragEnter={() => setOverId(id)}
              onDragLeave={() => setOverId((cur) => (cur === id ? null : cur))}
              onDrop={(e) => {
                e.preventDefault();
                setOverId(null);
                const raw = e.dataTransfer.getData(VARIABLE_DRAG_TYPE) || dragState.variable;
                if (!raw) return;
                const { fieldKey, sectionKey } = JSON.parse(raw);
                bind(f.pageId, f.members, fieldKey, sectionKey);
              }}
            >
              <div
                className="fd-fields-row-head"
                onClick={() => {
                  focusField(id);
                  setOpenId(open ? null : id);
                  setQuery('');
                }}
              >
                {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <span className="fd-fields-row-name" title={f.hint}>
                  {f.hint || `Field ${i + 1}`}
                  {f.members.length > 1 ? ` (${f.members.length} options)` : ''}
                  {doc.pages.length > 1 ? ` (p${f.pageNumber})` : ''}
                </span>
              </div>
              {boundList.map((b) => (
                <div key={b.fieldKey} className="fd-fields-row-value">
                  <span title={b.fieldKey}>{b.fieldKey}</span>
                  <button
                    type="button"
                    className="fd-fields-row-clear"
                    title="Remove this field"
                    onClick={() => unbindOne(f.pageId, f.members, b.fieldKey)}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {(boundList.length === 0 || addingId === id) && (
                <div className="fd-fields-row-drop">{boundList.length === 0 ? 'Drop field here' : 'Drop another field here'}</div>
              )}
              {boundList.length > 0 && (
                <button
                  type="button"
                  className="fd-fields-row-add"
                  onClick={() => {
                    setAddingId(addingId === id ? null : id);
                    setOpenId(id);
                    setQuery('');
                  }}
                >
                  <Plus size={11} /> Add field
                </button>
              )}
              {open && (
                <div className="fd-fields-picker">
                  <input
                    className="form-control form-control-sm mb-1"
                    placeholder="Search fields..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                  <div className="fd-fields-picker-list">
                    {sections.length === 0 && <div className="fd-variable-empty">No fields found</div>}
                    {sections.map(([sectionKey, names]) => (
                      <div key={sectionKey}>
                        <div className="fd-fields-picker-section">{sectionKey}</div>
                        {names.map((name) => (
                          <div
                            key={name}
                            className={`fd-fields-picker-item${boundList.some((b) => b.fieldKey === name) ? ' fd-fields-picker-current' : used.has(name) ? ' fd-fields-picker-used' : ''}`}
                            onClick={() => (boundList.some((b) => b.fieldKey === name) ? unbindOne(f.pageId, f.members, name) : bind(f.pageId, f.members, name, sectionKey))}
                          >
                            {used.has(name) ? '✓' : '•'} {name}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

